import { useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useDetailImage } from "@/lib/details";
import { TYPOLOGIES } from "@/lib/typology";

const JOINT_OPTS = [["", "Not a joint detail"], ["sliding+fixed", "Joint: sliding ↔ fixed"], ["sliding+casement", "Joint: sliding ↔ casement"], ["sliding+door", "Joint: sliding ↔ door"], ["casement+fixed", "Joint: casement ↔ fixed"], ["door+fixed", "Joint: door ↔ fixed"], ["coupling", "Joint: any coupling"]];

const AddDetail = ({ c, reload }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [joint, setJoint] = useState("sliding+fixed");
  const [title, setTitle] = useState("");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("title", title || file.name.replace(/\.[^.]+$/, "")); fd.append("joint", joint); fd.append("typology", "combination");
      await api.post(`/catalogues/${c.catalogue_id}/details/upload`, fd);
      await reload(); setTitle("");
      toast.success("Detail added");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-dashed border-slate-600 p-1.5 space-y-1" data-testid={`add-detail-${c.catalogue_id}`}>
      <input data-testid={`add-detail-title-${c.catalogue_id}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title e.g. Coupling sliding/fixed" className={sel} />
      <select data-testid={`add-detail-joint-${c.catalogue_id}`} value={joint} onChange={(e) => setJoint(e.target.value)} className={sel}>
        {JOINT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <button data-testid={`add-detail-btn-${c.catalogue_id}`} onClick={() => input.current?.click()} disabled={busy}
        className="h-12 w-full rounded border border-slate-700 text-[10px] text-slate-300 flex flex-col items-center justify-center hover:border-cyan-500 hover:text-cyan-300">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}Add detail (PNG/JPG/PDF page)
      </button>
      <input ref={input} type="file" hidden accept="image/png,image/jpeg,application/pdf" data-testid={`add-detail-file-${c.catalogue_id}`} onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
};

const sel = "h-6 w-full rounded bg-[#0B0F19] border border-slate-700 px-1 text-[10px] text-slate-100 focus:outline-none focus:border-cyan-500";

const DetailCard = ({ c, d, reload }) => {
  const src = useDetailImage({ ...d, catalogue_id: c.catalogue_id });
  const del = async () => {
    if (!window.confirm(`Remove "${d.title}"?`)) return;
    try { await api.delete(`/catalogues/${c.catalogue_id}/details/${d.detail_id}`); await reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (body) => {
    try { await api.patch(`/catalogues/${c.catalogue_id}/details/${d.detail_id}`, body); await reload(); toast.success("Section drawing updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-md border border-slate-700 p-1.5 space-y-1" data-testid={`catalogue-detail-${d.detail_id}`}>
      <div className="h-20 bg-white rounded grid place-items-center overflow-hidden">{src ? <img src={src} alt={d.title} className="max-h-20 object-contain" /> : <span className="text-[9px] text-slate-400">loading…</span>}</div>
      <p className="text-[10px] text-slate-200 truncate" title={d.title}>{d.title} · p.{d.page}</p>
      <div className="grid grid-cols-[1fr_40px] gap-1">
        <select data-testid={`detail-typology-${d.detail_id}`} value={d.typology} onChange={(e) => patch({ typology: e.target.value })} className={sel}>
          {TYPOLOGIES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input data-testid={`detail-shutters-${d.detail_id}`} type="number" min={1} defaultValue={d.shutters} title="Shutters / leaves"
          onBlur={(e) => { const n = parseInt(e.target.value, 10); if (n > 0 && n !== d.shutters) patch({ shutters: n }); }} className={sel} />
      </div>
      <div className="grid grid-cols-[1fr_24px] gap-1">
        <select data-testid={`detail-joint-${d.detail_id}`} value={d.joint || ""} onChange={(e) => patch({ joint: e.target.value })} className={sel} title="Combination joint">
          {JOINT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button data-testid={`detail-delete-${d.detail_id}`} title="Remove detail" onClick={del} className="h-6 grid place-items-center rounded text-slate-500 hover:text-rose-400"><Trash2 size={11} /></button>
      </div>
    </div>
  );
};

export const CatalogueDetails = ({ c, reload }) => (
  <div className="grid grid-cols-3 gap-2" data-testid={`catalogue-details-grid-${c.catalogue_id}`}>
    {(c.details || []).map((d) => <DetailCard key={d.detail_id} c={c} d={d} reload={reload} />)}
    <AddDetail c={c} reload={reload} />
  </div>
);
